import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatPrice, colorHex } from "@/lib/api";
import ProductImage from "@/components/ProductImage";

export const BADGES = {
  mais_vendido: { label: "⭐ Mais vendido", cls: "bg-gradient-to-r from-yellow-400 to-orange-400 text-white" },
  favorito: { label: "💗 Favorito", cls: "bg-pink-100 text-pink-600" },
  novidade: { label: "✨ Novidade", cls: "bg-gradient-to-r from-purple-400 to-pink-400 text-white" },
};

export default function ProductCard({ product, onAdd }) {
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useStore();
  const isFav = favorites.includes(product.id);
  const hasPromo = product.promo_price && product.promo_price > 0 && product.promo_price < product.price;
  const badge = BADGES[product.badge];
  const soldOut = !product.available;

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-hover-pink cursor-pointer flex flex-col"
      onClick={() => navigate(`/produto/${product.id}`)}
    >
      <div className="relative aspect-square overflow-hidden">
        <ProductImage src={product.images?.[0]} alt={product.name} className="transition-transform duration-500 group-hover:scale-105" />
        {soldOut && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
            <span data-testid="badge-esgotado" className="bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-full">Esgotado</span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {badge && !soldOut && (
            <span data-testid={`badge-${product.badge}`} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.cls}`}>
              {badge.label}
            </span>
          )}
          {hasPromo && !soldOut && (
            <span data-testid="badge-promo" className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-brand-gradient text-white">
              Promoção
            </span>
          )}
        </div>
        <button
          data-testid={`favorite-btn-${product.id}`}
          aria-label="Favoritar"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(product.id);
          }}
          className={`absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center shadow-soft transition-all duration-200 active:scale-90 ${
            isFav ? "bg-pink-500 text-white" : "bg-white/90 text-slate-400 hover:text-pink-500"
          }`}
        >
          <Heart className="w-4.5 h-4.5" size={18} fill={isFav ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <h3 className="font-bold text-slate-800 leading-snug line-clamp-2 text-sm sm:text-base">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-slate-500 line-clamp-2">{product.description}</p>
        )}
        {product.colors?.length > 0 && (
          <div className="flex gap-1 mt-0.5" data-testid="product-colors">
            {product.colors.slice(0, 5).map((c) => (
              <span
                key={c}
                title={c}
                className="w-3.5 h-3.5 rounded-full border border-slate-200"
                style={{ backgroundColor: colorHex(c) }}
              />
            ))}
          </div>
        )}
        <div className="mt-auto pt-2 flex items-end justify-between gap-2">
          <div>
            {hasPromo && (
              <span className="block text-xs text-slate-400 line-through">{formatPrice(product.price)}</span>
            )}
            <span data-testid="product-price" className={`font-extrabold ${hasPromo ? "text-pink-600" : "text-slate-900"} ${product.price > 0 ? "text-lg" : "text-sm"}`}>
              {formatPrice(hasPromo ? product.promo_price : product.price)}
            </span>
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            disabled={soldOut}
            onClick={(e) => {
              e.stopPropagation();
              if (!soldOut) onAdd?.(product);
            }}
            className={`text-xs sm:text-sm font-bold px-3.5 py-2 rounded-full transition-all duration-200 active:scale-95 ${
              soldOut
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-brand-gradient text-white shadow-soft hover:opacity-90"
            }`}
          >
            {soldOut ? "Indisponível" : "Adicionar"}
          </button>
        </div>
      </div>
    </div>
  );
}
